import { useState } from "react"
import type { RelistRunInboxStatus } from "@nova-ai/shared"
import { Link } from "@tanstack/react-router"
import { ArrowRight, ChevronRight } from "lucide-react"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { cn } from "../../lib/utils"

export function AutomaticInboxStatus({
  status,
  isLoading,
  error,
}: {
  status: RelistRunInboxStatus | undefined
  isLoading: boolean
  error: Error | null
}) {
  const [expanded, setExpanded] = useState(false)
  if (isLoading) {
    return (
      <div
        className="mb-6 h-12 animate-pulse rounded-lg bg-muted [animation-duration:1.2s] [animation-timing-function:ease-in-out] motion-reduce:animate-none"
        aria-label="Loading auto relist status"
      />
    )
  }
  if (error || !status) {
    return (
      <div
        className="mb-6 rounded-lg border border-border bg-card px-4 py-3 text-muted-foreground"
        role="status"
      >
        {"Auto relist status unavailable. "}

        <Link
          className="focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
          to="/settings/listing-automation"
        >
          Manage
        </Link>
      </div>
    )
  }
  const markets = status.marketplaces
  const saved = markets.filter((market) => market.enabled).length
  const effective = markets.filter((market) => market.effectiveEnabled).length
  const restricted = markets.filter((market) => !market.executionEnabled).length
  const queued = markets.reduce((total, market) => total + market.pending + market.claimed, 0)
  const blocked = markets.reduce((total, market) => total + market.blocked, 0)
  let summary = `${effective} of ${markets.length} on`
  if (saved === 0) {
    summary = `Off for all ${markets.length} marketplaces`
  }
  if (markets.length === 0) {
    summary = "No marketplaces configured"
  }
  const restriction =
    restricted === markets.length
      ? "Blocked by this deployment"
      : `${restricted} deployment restricted`
  return (
    <section
      data-runs-automation
      className="mb-6 rounded-lg border border-border bg-card"
      aria-label="Auto relist"
    >
      <div className="flex flex-col flex-wrap items-stretch text-[13px] sm:flex-row sm:items-center sm:gap-x-4 sm:gap-y-2 sm:px-4 sm:py-3">
        <div className="flex min-w-0 flex-1 flex-col flex-wrap items-start gap-0.5 px-4 py-3 sm:min-w-[200px] sm:flex-row sm:items-center sm:gap-x-4 sm:gap-y-1 sm:p-0">
          <span className="font-semibold">Auto relist</span>

          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-medium whitespace-nowrap",
                effective > 0 ? "text-success" : "text-muted-foreground"
              )}
            >
              <span className="size-1.5 flex-none rounded-full bg-current" />

              {summary}
            </span>

            {saved > 0 && (
              <span className="text-muted-foreground tabular-nums">{`· ${queued} queued · ${blocked} blocked`}</span>
            )}

            {restricted > 0 && <span className="text-muted-foreground">· {restriction}</span>}

            {saved !== effective && (
              <span className="text-muted-foreground">· {saved} saved on</span>
            )}
          </span>
        </div>

        <div className="flex min-h-11 items-center justify-between gap-4 border-t border-border px-4 sm:min-h-0 sm:justify-normal sm:border-0 sm:p-0">
          <button
            type="button"
            className="group/disclosure inline-flex min-h-11 cursor-pointer items-center gap-1 text-[13px] text-muted-foreground sm:min-h-0 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
            aria-expanded={expanded}
            aria-controls="relist-marketplaces"
            onClick={() => setExpanded(!expanded)}
          >
            <span>Show marketplaces</span>

            <ChevronRight className="size-3 flex-none transition-transform duration-200 ease-product-out group-aria-expanded/disclosure:rotate-90 motion-reduce:transition-none" />
          </button>

          <Link
            className="inline-flex min-h-11 items-center gap-1 text-[13px] font-medium no-underline hover:underline hover:underline-offset-3 sm:min-h-0 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
            to="/settings/listing-automation"
          >
            Manage <ArrowRight className="size-3 flex-none" />
          </Link>
        </div>
      </div>

      <div
        id="relist-marketplaces"
        className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border px-4 pt-3 pb-3.5 [&[hidden]]:hidden sm:flex sm:flex-wrap sm:gap-1.5 sm:border-0 sm:pt-0"
        hidden={!expanded}
      >
        {markets.map((market) => (
          <span
            className="inline-flex min-w-0 items-center gap-1.5 text-[12px] sm:rounded-full sm:border sm:border-border sm:px-2.5 sm:py-0.5 sm:whitespace-nowrap"
            key={market.marketplace}
            title={`Saved ${market.enabled ? "on" : "off"}; ${market.effectiveEnabled ? "execution permitted" : "execution paused"}${market.executionEnabled ? "" : "; deployment restricted"}`}
          >
            <span
              className={cn(
                "size-1.5 flex-none rounded-full bg-current",
                market.effectiveEnabled ? "text-success" : "text-muted-foreground"
              )}
            />

            {humanizeMarketplaceId(market.marketplace)}

            <span className="sr-only">
              {market.effectiveEnabled ? "execution permitted" : "execution paused"}
            </span>

            {market.pending + market.claimed > 0 && (
              <b className="tabular-nums">{market.pending + market.claimed}</b>
            )}
          </span>
        ))}
      </div>
    </section>
  )
}
